import { useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import AuthMedia from "./AuthMedia";
import { Plus, User, ZoomIn, ZoomOut, Locate, Link2, X, TreePine } from "lucide-react";

export const CANVAS_W = 4000;
export const CANVAS_H = 3000;
const NODE_W = 190;
const NODE_H = 150; // approx, used only for line anchoring
const SAME_LINE_DY = 85; // vertical tolerance to treat two linked people as a couple (same line)
const STROKE = "#c9a892";

const XMark = ({ x, y, onClick, testid }) => (
  <g className="tree-ctrl" style={{ pointerEvents: "all", cursor: "pointer" }} onClick={onClick} data-testid={testid}>
    <circle cx={x} cy={y} r={10} fill="#fff" stroke="#e3ddd3" />
    <line x1={x - 3.5} y1={y - 3.5} x2={x + 3.5} y2={y + 3.5} stroke="#c9694f" strokeWidth={2} />
    <line x1={x + 3.5} y1={y - 3.5} x2={x - 3.5} y2={y + 3.5} stroke="#c9694f" strokeWidth={2} />
  </g>
);

const NodeCard = ({ node, pos, isReadOnly, guardianToken, linking, isLinkSource, onPointerDown, onStartLink }) => {
  const p = pos || { x: node.x || 0, y: node.y || 0 };
  const years = node.birth_year || node.death_year ? `${node.birth_year || "?"} – ${node.death_year || "?"}` : null;
  return (
    <div
      className="node-card absolute"
      style={{ left: p.x, top: p.y, width: NODE_W, touchAction: "none" }}
      onPointerDown={(e) => onPointerDown(e, node)}
      data-testid={`tree-node-${node.id}`}
    >
      <div
        style={{ boxShadow: "0 6px 18px rgba(44,42,41,0.08)" }}
        className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl border text-center select-none transition-shadow
          ${node.is_self ? "bg-[#FBEDE7]" : "bg-white"}
          ${isLinkSource ? "border-primary ring-2 ring-primary/40" : node.is_self ? "border-primary/70 ring-2 ring-primary/25" : "border-[#e3ddd3]"}
          ${isReadOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing hover:shadow-lg"}
          ${linking && !isLinkSource ? "hover:border-primary" : ""}`}
      >
        {node.is_self && (
          <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-medium tracking-wide uppercase bg-primary text-white px-2 py-0.5 rounded-full shadow-sm">Io</span>
        )}
        <div className={`w-16 h-16 rounded-full overflow-hidden border flex items-center justify-center shrink-0 pointer-events-none ${node.is_self ? "border-primary/40 bg-white" : "border-[#e3ddd3] bg-[#F4F1EA]"}`}>
          {node.photo ? <AuthMedia item={node.photo} guardianToken={guardianToken} className="w-full h-full object-cover" /> : <User className={`w-7 h-7 ${node.is_self ? "text-primary" : "text-[#c9a892]"}`} />}
        </div>
        <div className="min-w-0 w-full pointer-events-none">
          <p className="font-serif text-base leading-tight text-[#2C2A29] truncate">{node.name}</p>
          {node.relation ? <p className="text-xs mt-0.5 text-primary truncate">{node.relation}</p> : null}
          {years && <p className="text-xs text-muted-foreground mt-0.5">{years}</p>}
        </div>
        {!isReadOnly && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onStartLink(node.id); }}
            title="Collega a un altro riquadro"
            className="link-handle absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#81B29A] text-white flex items-center justify-center shadow hover:bg-[#6ba084]"
            data-testid={`tree-link-${node.id}`}
          >
            <Link2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const FamilyTree = ({
  nodes = [],
  links = [],
  isReadOnly = false,
  guardianToken,
  onNodeClick,
  onNodeMove,
  onCreateLink,
  onDeleteLink,
}) => {
  const wrapperRef = useRef(null);
  const dragRef = useRef(null);
  const [pos, setPos] = useState({});
  const [linkFrom, setLinkFrom] = useState(null);

  const getScale = () => wrapperRef.current?.instance?.transformState?.scale || 1;
  const posOf = (n) => pos[n.id] || { x: n.x || 0, y: n.y || 0 };

  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const s = getScale();
    const nx = d.ox + (e.clientX - d.sx) / s;
    const ny = d.oy + (e.clientY - d.sy) / s;
    if (Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 5) d.moved = true;
    d.nx = nx; d.ny = ny;
    setPos((prev) => ({ ...prev, [d.id]: { x: nx, y: ny } }));
  };

  const onUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    if (!d) return;
    if (d.moved) {
      onNodeMove && onNodeMove(d.id, Math.round(d.nx), Math.round(d.ny));
      return;
    }
    // treated as a click
    if (linkFrom) {
      if (linkFrom !== d.id) {
        onCreateLink && onCreateLink(linkFrom, d.id);
        // If the two people are roughly on the same line, treat them as a couple
        // and snap the just-linked node next to the first, perfectly aligned.
        const s = nodes.find((n) => n.id === linkFrom);
        const t = nodes.find((n) => n.id === d.id);
        if (s && t) {
          const sp = pos[s.id] || { x: s.x || 0, y: s.y || 0 };
          const tp = pos[t.id] || { x: t.x || 0, y: t.y || 0 };
          if (Math.abs(sp.y - tp.y) <= SAME_LINE_DY) {
            const toRight = tp.x >= sp.x;
            const nx = Math.round(sp.x + (toRight ? NODE_W + 60 : -(NODE_W + 60)));
            const ny = Math.round(sp.y);
            setPos((p) => ({ ...p, [t.id]: { x: nx, y: ny } }));
            onNodeMove && onNodeMove(t.id, nx, ny);
          }
        }
      }
      setLinkFrom(null);
    } else {
      onNodeClick && onNodeClick(d.node);
    }
  };

  const onPointerDown = (e, node) => {
    if (isReadOnly) { onNodeClick && onNodeClick(node); return; }
    e.stopPropagation();
    const start = posOf(node);
    dragRef.current = { id: node.id, node, sx: e.clientX, sy: e.clientY, ox: start.x, oy: start.y, nx: start.x, ny: start.y, moved: false };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const empty = nodes.length === 0;

  // --- Genealogical link geometry ---
  // Couples = two linked people roughly on the same horizontal line.
  // Children branch from the couple's union line (or a single parent's base).
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n; });
  const P = (id) => pos[id] || { x: byId[id]?.x || 0, y: byId[id]?.y || 0 };
  const cX = (id) => P(id).x + NODE_W / 2;
  const cY = (id) => P(id).y + NODE_H / 2;
  const topY = (id) => P(id).y + 6;
  const botY = (id) => P(id).y + NODE_H - 6;

  const spouseLinks = [];
  const vertLinks = [];
  links.forEach((l) => {
    if (!byId[l.a] || !byId[l.b]) return;
    if (Math.abs(cY(l.a) - cY(l.b)) <= SAME_LINE_DY) spouseLinks.push(l);
    else {
      const parent = cY(l.a) < cY(l.b) ? l.a : l.b;
      const child = parent === l.a ? l.b : l.a;
      vertLinks.push({ id: l.id, parent, child });
    }
  });

  const coupleByMember = {};
  const groups = {};
  spouseLinks.forEach((l) => {
    const key = [l.a, l.b].sort().join("~");
    if (!groups[key]) groups[key] = { type: "couple", members: [l.a, l.b], linkId: l.id, children: {} };
    if (!coupleByMember[l.a]) coupleByMember[l.a] = key;
    if (!coupleByMember[l.b]) coupleByMember[l.b] = key;
  });
  vertLinks.forEach((v) => {
    let key = coupleByMember[v.parent];
    if (!key || !groups[key]) {
      key = "single~" + v.parent;
      if (!groups[key]) groups[key] = { type: "single", members: [v.parent], children: {} };
    }
    const g = groups[key];
    if (!g.children[v.child]) g.children[v.child] = [];
    g.children[v.child].push(v.id);
  });

  const linkEls = [];
  Object.entries(groups).forEach(([key, g]) => {
    let ux, uy;
    if (g.type === "couple") {
      const [a, b] = g.members;
      linkEls.push(<line key={"m-" + key} x1={cX(a)} y1={cY(a)} x2={cX(b)} y2={cY(b)} stroke={STROKE} strokeWidth={3} />);
      ux = (cX(a) + cX(b)) / 2; uy = (cY(a) + cY(b)) / 2;
      if (!isReadOnly) linkEls.push(<XMark key={"mx-" + key} x={ux} y={uy} onClick={() => onDeleteLink && onDeleteLink(g.linkId)} testid={`tree-unlink-${g.linkId}`} />);
    } else {
      const p = g.members[0];
      ux = cX(p); uy = botY(p);
    }
    const kids = Object.keys(g.children);
    if (kids.length) {
      const childXs = kids.map(cX);
      const childTops = kids.map(topY);
      let busY = Math.min(...childTops) - 30;
      if (busY < uy + 24) busY = uy + 24;
      const minX = Math.min(ux, ...childXs);
      const maxX = Math.max(ux, ...childXs);
      linkEls.push(<line key={"drop-" + key} x1={ux} y1={uy} x2={ux} y2={busY} stroke={STROKE} strokeWidth={2.5} />);
      if (maxX - minX > 1) linkEls.push(<line key={"bus-" + key} x1={minX} y1={busY} x2={maxX} y2={busY} stroke={STROKE} strokeWidth={2.5} />);
      kids.forEach((cid) => {
        linkEls.push(<line key={"cdrop-" + key + "-" + cid} x1={cX(cid)} y1={busY} x2={cX(cid)} y2={topY(cid)} stroke={STROKE} strokeWidth={2.5} />);
        if (!isReadOnly) {
          const my = (busY + topY(cid)) / 2;
          g.children[cid].forEach((lid, k) => {
            linkEls.push(<XMark key={"cx-" + lid} x={cX(cid) + k * 16} y={my} onClick={() => onDeleteLink && onDeleteLink(lid)} testid={`tree-unlink-${lid}`} />);
          });
        }
      });
    }
  });

  return (
    <TransformWrapper
      ref={wrapperRef}
      minScale={0.25}
      maxScale={2}
      initialScale={0.7}
      centerOnInit
      limitToBounds={false}
      doubleClick={{ disabled: true }}
      panning={{ excluded: ["node-card", "link-handle", "tree-ctrl"] }}
    >
      {({ zoomIn, zoomOut, resetTransform }) => (
        <>
          <div className="tree-ctrl absolute top-4 right-4 z-20 flex flex-col gap-2">
            <button onClick={() => zoomIn()} className="w-10 h-10 rounded-full bg-white border border-border shadow-sm flex items-center justify-center hover:bg-[#F4F1EA]" data-testid="tree-zoom-in"><ZoomIn className="w-4 h-4" /></button>
            <button onClick={() => zoomOut()} className="w-10 h-10 rounded-full bg-white border border-border shadow-sm flex items-center justify-center hover:bg-[#F4F1EA]" data-testid="tree-zoom-out"><ZoomOut className="w-4 h-4" /></button>
            <button onClick={() => resetTransform()} className="w-10 h-10 rounded-full bg-white border border-border shadow-sm flex items-center justify-center hover:bg-[#F4F1EA]" data-testid="tree-reset"><Locate className="w-4 h-4" /></button>
          </div>

          {linkFrom && (
            <div className="tree-ctrl absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-[#2C2A29] text-white text-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-3" data-testid="tree-link-hint">
              <span>Clicca il riquadro da collegare</span>
              <button onClick={() => setLinkFrom(null)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          )}

          <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: CANVAS_W, height: CANVAS_H }}>
            <div className="relative" style={{ width: CANVAS_W, height: CANVAS_H }}>
              <svg className="absolute inset-0 pointer-events-none" width={CANVAS_W} height={CANVAS_H} style={{ overflow: "visible" }}>
                {linkEls}
              </svg>

              {nodes.map((n) => (
                <NodeCard
                  key={n.id}
                  node={n}
                  pos={posOf(n)}
                  isReadOnly={isReadOnly}
                  guardianToken={guardianToken}
                  linking={!!linkFrom}
                  isLinkSource={linkFrom === n.id}
                  onPointerDown={onPointerDown}
                  onStartLink={setLinkFrom}
                />
              ))}

              {empty && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-muted-foreground max-w-sm px-6">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#EAF3EE] flex items-center justify-center">
                      <TreePine className="w-7 h-7 text-[#4e8f6f]" />
                    </div>
                    <p className="font-serif text-xl text-[#2C2A29]">Il tuo albero è vuoto</p>
                    <p className="text-sm mt-1">Usa il pulsante <span className="inline-flex items-center gap-1 font-medium text-primary"><Plus className="w-3.5 h-3.5" />Nuovo</span> per aggiungere la prima persona, poi trascina i riquadri e collegali tra loro.</p>
                  </div>
                </div>
              )}
            </div>
          </TransformComponent>
        </>
      )}
    </TransformWrapper>
  );
};

export default FamilyTree;
